import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../App';
import ModeToggle from './ModeToggle';
import ShelterCareConnectLogo from './ui/logo';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import ReportIssueDialog from './ReportIssueDialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import {
  Home,
  Users,
  Building,
  FileText,
  HelpCircle,
  LogOut,
  Menu,
  X,
  Shield,
  Clock,
  UserCheck,
  UtensilsCrossed,
  ListTodo,
  Shirt,
  BarChart3,
  UserX,
  AlertTriangle,
  Wrench,
  MessageSquarePlus,
  Gauge,
  Package,
  Salad,
  Heart,
  PenTool,
  FileDown,
  FolderOpen,
  CreditCard,
  ChevronDown
} from 'lucide-react';

const Navigation = ({ onHelpClick }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);

  // Grouped navigation — category headers with dropdowns keep the top bar
  // readable at 100% zoom. `href` groups are direct links; others open a menu.
  const navGroups = [
    { label: 'Dashboard', icon: Home, href: '/dashboard' },
    {
      label: 'Residents',
      icon: Users,
      items: [
        { name: 'Residents', href: '/residents', icon: Users, description: 'Manage resident information' },
        { name: 'Case Notes', href: '/case-notes', icon: FileText, description: 'Resident case documentation' },
        { name: 'Change Requests', href: '/change-requests', icon: MessageSquarePlus, description: 'After-hours change requests for review' },
        { name: 'Incident Reports', href: '/incident-reports', icon: AlertTriangle, description: 'Safety incident tracking and reports' },
        { name: 'Consent Forms', href: '/consent', icon: PenTool, description: 'Digital ROI, house rules & CoC signatures' },
        { name: 'Banned Residents', href: '/banned-residents', icon: UserX, description: 'Manage banned residents' },
      ],
    },
    {
      label: 'Rooms',
      icon: Building,
      items: [
        { name: 'Rooms & Beds', href: '/rooms', icon: Building, description: 'Room assignments and bed availability' },
        { name: 'Maintenance', href: '/maintenance-requests', icon: Wrench, description: 'Facility maintenance and repair requests' },
      ],
    },
    {
      label: 'Meals',
      icon: UtensilsCrossed,
      items: [
        { name: 'Meals', href: '/meals', icon: UtensilsCrossed, description: 'Meal planning, portioning and cost tracking' },
        { name: 'Food Inventory', href: '/inventory', icon: Package, description: 'Food & supply stock, costs and alerts' },
        { name: 'Dietary Needs', href: '/dietary', icon: Salad, description: 'Resident dietary restrictions and allergies' },
      ],
    },
    {
      label: 'Staff',
      icon: UserCheck,
      items: [
        { name: 'Staff', href: '/staff', icon: UserCheck, description: 'Staff management and time clock' },
        { name: 'Chores', href: '/chores', icon: ListTodo, description: 'Chore assignments and completion' },
        { name: 'Laundry', href: '/laundry', icon: Shirt, description: 'Laundry schedules and supplies' },
        { name: 'Shift Logs', href: '/shift-logs', icon: FileText, description: 'View shift handoff notes and reports' },
        { name: 'Time Reports', href: '/time-reports', icon: BarChart3, description: 'Staff time tracking reports' },
        { name: 'Admin Time Mgmt', href: '/admin-time-management', icon: Clock, description: 'Time entry management and corrections', adminOnly: true },
      ],
    },
    {
      label: 'More',
      icon: Menu,
      items: [
        { name: 'Volunteers', href: '/volunteers', icon: Heart, description: 'Volunteers, hours & in-kind match reporting' },
        { name: 'Document Center', href: '/documents', icon: FolderOpen, description: 'Shelter policies, licenses & forms' },
        { name: 'HMIS Exports', href: '/hmis-exports', icon: FileDown, description: 'HUD-formatted CSV exports for reporting', adminOnly: true },
        { name: 'Billing & Plan', href: '/billing', icon: CreditCard, description: 'Subscription, payment method & bed capacity', adminOnly: true },
        { name: 'Platform Admin', href: '/platform', icon: Gauge, description: 'All shelters, usage & support tickets', ownerOnly: true },
      ],
    },
  ];

  const canSee = (item) =>
    (!item.adminOnly || user?.role === 'admin') &&
    (!item.ownerOnly || user?.is_platform_owner);

  // Flat list (respecting permissions) for the mobile menu
  const allNavigationItems = navGroups.flatMap((g) =>
    g.href ? [{ name: g.label, href: g.href, icon: g.icon, description: '' }] : g.items
  );

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'case_worker':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'general_staff':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      default:
        return 'bg-blue-100 text-blue-800 border-blue-200';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin':
        return Shield;
      case 'case_worker':
        return UserCheck;
      case 'general_staff':
        return Clock;
      default:
        return Clock;
    }
  };

  const isActive = (href) => {
    return location.pathname === href;
  };

  const handleLogout = () => {
    logout();
    setMobileMenuOpen(false);
  };

  const RoleIcon = getRoleIcon(user?.role);

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo and brand */}
            <ShelterCareConnectLogo size="md" showText={true} />

            {/* Desktop navigation — grouped category dropdowns */}
            <div className="hidden lg:flex items-center space-x-1">
              {navGroups.map((group) => {
                const GroupIcon = group.icon;

                // Direct link group (e.g., Dashboard)
                if (group.href) {
                  return (
                    <Link
                      key={group.label}
                      to={group.href}
                      className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        isActive(group.href)
                          ? 'bg-indigo-100 text-indigo-700 border border-indigo-200'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                      }`}
                    >
                      <GroupIcon className="h-4 w-4" />
                      <span>{group.label}</span>
                    </Link>
                  );
                }

                const visibleItems = group.items.filter(canSee);
                if (visibleItems.length === 0) return null;
                const groupActive = visibleItems.some((it) => isActive(it.href));

                return (
                  <DropdownMenu key={group.label}>
                    <DropdownMenuTrigger asChild>
                      <button
                        className={`flex items-center space-x-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors focus:outline-none ${
                          groupActive
                            ? 'bg-indigo-100 text-indigo-700 border border-indigo-200'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                        }`}
                        data-testid={`nav-group-${group.label.toLowerCase()}`}
                      >
                        <GroupIcon className="h-4 w-4" />
                        <span>{group.label}</span>
                        <ChevronDown className="h-3.5 w-3.5 opacity-70" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-64">
                      {visibleItems.map((item) => {
                        const Icon = item.icon;
                        return (
                          <DropdownMenuItem key={item.name} asChild>
                            <Link
                              to={item.href}
                              className={`flex items-start space-x-2 px-2 py-2 text-sm ${
                                isActive(item.href)
                                  ? 'bg-indigo-100 text-indigo-700'
                                  : 'text-gray-600 hover:text-gray-900'
                              }`}
                              data-testid={`nav-item-${item.href.replace(/\//g, '')}`}
                            >
                              <Icon className="h-4 w-4 mt-0.5 shrink-0" />
                              <div>
                                <span className="font-medium">{item.name}</span>
                                <p className="text-xs text-gray-500 mt-0.5">{item.description}</p>
                              </div>
                            </Link>
                          </DropdownMenuItem>
                        );
                      })}
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              })}
            </div>

            {/* User menu and actions */}
            <div className="flex items-center space-x-3">
              {/* Mode Toggle */}
              <ModeToggle />
              
              {/* Report an issue */}
              <ReportIssueDialog />
              
              {/* Help button (admins only — opens AI Help Assistant) */}
              {(user?.role === 'admin' || user?.is_platform_owner) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onHelpClick}
                  className="hidden sm:flex items-center space-x-2 text-gray-600 hover:text-indigo-600"
                  title="Ask the Help Assistant"
                  data-testid="nav-help-btn"
                >
                  <HelpCircle className="h-4 w-4" />
                  <span className="hidden lg:inline">Help</span>
                </Button>
              )}

              {/* User info */}
              <div className="hidden lg:flex items-center space-x-3 pl-3 border-l border-gray-200">
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900">{user?.full_name}</p>
                  <div className="flex items-center space-x-1">
                    <RoleIcon className="h-3 w-3" />
                    <Badge className={`text-xs px-2 py-0.5 ${getRoleColor(user?.role)}`}>
                      {user?.role === 'case_worker' ? 'CASE MANAGER' : user?.role?.replace('_', ' ').toUpperCase()}
                    </Badge>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleLogout}
                  className="text-gray-600 hover:text-red-600"
                  title="Sign out"
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>

              {/* Mobile menu button */}
              <Button
                variant="ghost"
                size="sm"
                className="lg:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? (
                  <X className="h-5 w-5" />
                ) : (
                  <Menu className="h-5 w-5" />
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-gray-200 bg-white">
            <div className="px-4 py-3 space-y-1">
              {allNavigationItems
                .filter(item => (!item.adminOnly || user?.role === 'admin') && (!item.ownerOnly || user?.is_platform_owner))
                .map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center space-x-3 px-3 py-3 rounded-lg text-base font-medium ${
                      isActive(item.href)
                        ? 'bg-indigo-100 text-indigo-700'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    <div>
                      <span>{item.name}</span>
                      <p className="text-xs text-gray-500 mt-0.5">{item.description}</p>
                    </div>
                  </Link>
                );
              })}
              
              {/* Mobile help button (admins only) */}
              {(user?.role === 'admin' || user?.is_platform_owner) && (
                <button
                  onClick={() => {
                    onHelpClick();
                    setMobileMenuOpen(false);
                  }}
                  className="flex items-center space-x-3 px-3 py-3 rounded-lg text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 w-full"
                  data-testid="mobile-nav-help-btn"
                >
                  <HelpCircle className="h-5 w-5" />
                  <span>Help Assistant</span>
                </button>
              )}
              
              {/* Mobile user info and logout */}
              <div className="border-t border-gray-200 pt-3 mt-3">
                <div className="flex items-center justify-between px-3 py-2">
                  <div>
                    <p className="text-base font-medium text-gray-900">{user?.full_name}</p>
                    <div className="flex items-center space-x-1 mt-1">
                      <RoleIcon className="h-3 w-3" />
                      <Badge className={`text-xs px-2 py-0.5 ${getRoleColor(user?.role)}`}>
                        {user?.role?.replace('_', ' ').toUpperCase()}
                      </Badge>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleLogout}
                    className="text-gray-600 hover:text-red-600"
                  >
                    <LogOut className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

export default Navigation;